import React, { useMemo } from 'react';
import './UserDisplay.scss';
import { Avatar } from 'primereact/avatar';
import { getUserAvatarProps } from '../../utils/avatarUtils';
import { Button, ButtonProps } from 'primereact/button';
import classNames from 'classnames';
import { useNavigateScroll } from '../../hooks/useNavigateScroll';

interface BaseProps {
  avatarUrl?: string;
  name?: string;
  surnames?: string;
  fullName?: string;
  buttonProps?: ButtonProps;
  isPlaceholder?: boolean;
  displayReversed?: boolean;
  bookedFromApp?: boolean;
  navigateTo?: string;
  phone?: string;
}
interface FullNameProps extends BaseProps {
  fullName: string | undefined;
  name?: never;
  surnames?: never;
}
interface NameSurnamesProps extends BaseProps {
  name: string | undefined;
  surnames: string | undefined;
  fullName?: never;
}

type Props = FullNameProps | NameSurnamesProps;

export const UserDisplay = ({
  avatarUrl,
  name,
  surnames,
  fullName,
  buttonProps,
  isPlaceholder,
  displayReversed,
  bookedFromApp,
  navigateTo,
  phone,
}: Props) => {
  const navigate = useNavigateScroll();
  const [inferredName, inferredSurnames] = fullName?.split(' ') || [];

  const userAvatarProps = useMemo(
    () =>
      getUserAvatarProps({
        name: name || inferredName,
        surnames: surnames || inferredSurnames,
        avatarUrl,
        isPlaceholder,
      }),
    [name, surnames, inferredName, inferredSurnames, avatarUrl, isPlaceholder],
  );

  const handleClick = () => {
    if (navigateTo) {
      navigate(navigateTo);
    }
  };

  return (
    <div
      className={classNames('user-display align-center half-gap', {
        'flex-reverse': displayReversed,
      })}
    >
      <Avatar shape='circle' {...userAvatarProps} />
      <div className='user-info'>
        <p {...(navigateTo && { onClick: handleClick })} className={classNames('user-name', { clickable: navigateTo })}>
          {fullName || `${name} ${surnames}`}
          {bookedFromApp ? ' (app)' : ''}
        </p>
        {phone && (
          <div className='user-phone-container'>
            <i className='pi pi-phone' />
            <p className='user-phone'>{phone}</p>
          </div>
        )}
      </div>
      {buttonProps && <Button {...buttonProps} />}
    </div>
  );
};
